import { ErrorMessage } from '@hookform/error-message';
import { type FC, useCallback, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Row from 'react-bootstrap/Row';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import EyeHideIcon from '../../../assets/eye-hide.svg';
import EyeShowIcon from '../../../assets/eye-show.svg';
import { useAuth, useNotifications } from '../../../modules/hooks';
import { routes } from '../../../modules/mappers/urls';
import { LogoIcon } from '../../Logo';

import './styles/createNewPassword.css';

interface CreateNewPasswordForm {
  email: string;
  code: string;
  password1: string;
  password2: string;
}

export const CreateNewPassword: FC = () => {
  const { forgotPasswordSubmit } = useAuth()!;
  const { setSimpleToasts } = useNotifications()!;
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [isPassShow, setIsPassShow] = useState(false);
  const [isConfirmPassShow, setIsConfirmPassShow] = useState(false);

  const {
    handleSubmit,
    register,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<CreateNewPasswordForm>({ mode: 'onChange' });

  const onSubmit = async (data: CreateNewPasswordForm) => {
    const validated = await trigger(['email', 'code', 'password1', 'password2']);
    if (!validated) {
      return;
    }

    const { email, code, password1 } = data;
    setIsLoading(true);

    try {
      await forgotPasswordSubmit(email, code, password1);
      setSimpleToasts({
        type: 'success',
        message:
          'Password has been reset successfully. Please sign in with your new password.',
        show: true,
      });
      navigate(routes.LOGIN);
    } catch (e) {
      const { message } = e as Error;
      setSimpleToasts({
        type: 'danger',
        message: message || 'Failed to reset password. Please try again.',
        show: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onPasswordToggle = useCallback(() => {
    setIsPassShow(!isPassShow);
  }, [isPassShow]);

  const onConfirmPasswordToggle = useCallback(() => {
    setIsConfirmPassShow(!isConfirmPassShow);
  }, [isConfirmPassShow]);

  return (
    <Container
      fluid
      className="login forgot-password d-flex flex-column justify-content-center"
    >
      <Container className="content" fluid>
        <Row className="justify-content-md-center loginLogo">
          <Col />
          <Col className="justify-content-md-center" xs="auto" sm="auto">
            <LogoIcon width="49.5px" height="45px" />
          </Col>
          <Col />
        </Row>
        <Row className="justify-content-md-center forgotPassHeader text-center">
          <Col />
          <Col className="justify-content-md-center" xs="auto" sm="auto">
            <h3 className="login-heading">Create new Password</h3>
            <p>Your new password must be different from previous used passwords.</p>
          </Col>
          <Col />
        </Row>
        <Row>
          <Col>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group email-field">
                <input
                  type="email"
                  className="form-control"
                  id="email"
                  placeholder="Enter email address"
                  {...register('email', {
                    required: 'Email is required.',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Please enter a valid email address.',
                    },
                  })}
                />
                <ErrorMessage
                  errors={errors}
                  name="email"
                  render={({ message }) => (
                    <span
                      className="password-error-text"
                      style={{
                        color: 'red',
                        fontSize: '12px',
                        display: 'block',
                        marginTop: '4px',
                      }}
                    >
                      {message}
                    </span>
                  )}
                />
              </div>
              <div className="form-group email-field">
                <input
                  type="text"
                  className="form-control"
                  id="verificationCode"
                  placeholder="Enter verification code"
                  {...register('code', {
                    required: 'Verification code is required.',
                  })}
                />
                <ErrorMessage
                  errors={errors}
                  name="code"
                  render={({ message }) => (
                    <span
                      className="password-error-text"
                      style={{
                        color: 'red',
                        fontSize: '12px',
                        display: 'block',
                        marginTop: '4px',
                      }}
                    >
                      {message}
                    </span>
                  )}
                />
              </div>
              <div className="form-group create-password">
                <InputGroup>
                  <Form.Control
                    type={isPassShow ? 'text' : 'password'}
                    className="form-control"
                    id="exampleInputPassword1"
                    placeholder="New Password"
                    {...register('password1', {
                      required: 'Password is required.',
                      pattern: {
                        value: /^(?=.*[\W_])(?=.*\d)(?=.*[A-Z]).{9,}$/,
                        message:
                          'Password must contain: one number, one special character, one uppercase letter and be at least 9 characters long',
                      },
                    })}
                  />
                  <InputGroup.Text
                    onClick={onPasswordToggle}
                    style={{ cursor: 'pointer' }}
                  >
                    <img
                      className="eye-icon-container"
                      alt="eye-img"
                      src={isPassShow ? EyeHideIcon : EyeShowIcon}
                      style={{ width: '20px', height: '20px' }}
                    />
                  </InputGroup.Text>
                </InputGroup>
                <ErrorMessage
                  errors={errors}
                  name="password1"
                  render={({ message }) => (
                    <span
                      className="password-error-text"
                      style={{
                        color: 'red',
                        fontSize: '12px',
                        display: 'block',
                        marginTop: '4px',
                      }}
                    >
                      {message}
                    </span>
                  )}
                />
                <InputGroup>
                  <Form.Control
                    type={isConfirmPassShow ? 'text' : 'password'}
                    className="form-control"
                    id="exampleInputPassword2"
                    placeholder="Confirm Password"
                    style={{
                      borderTop: '1px solid #d1d5db',
                      borderRadius: '0 0 6px 6px',
                    }}
                    {...register('password2', {
                      required: 'Please confirm your password.',
                      validate: (value) =>
                        value === getValues('password1') || 'Both passwords must match',
                    })}
                  />
                  <InputGroup.Text
                    onClick={onConfirmPasswordToggle}
                    style={{ cursor: 'pointer', borderTop: '1px solid #d1d5db' }}
                  >
                    <img
                      className="eye-icon-container"
                      alt="eye-img"
                      src={isConfirmPassShow ? EyeHideIcon : EyeShowIcon}
                      style={{ width: '20px', height: '20px' }}
                    />
                  </InputGroup.Text>
                </InputGroup>
                <ErrorMessage
                  errors={errors}
                  name="password2"
                  render={({ message }) => (
                    <span
                      className="password-error-text"
                      style={{
                        color: 'red',
                        fontSize: '12px',
                        display: 'block',
                        marginTop: '4px',
                      }}
                    >
                      {message}
                    </span>
                  )}
                />
              </div>
              <button
                disabled={isLoading}
                type="submit"
                className="btn submit-btn position-relative"
              >
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className={isLoading ? '' : 'd-none'}
                />
                <span className={isLoading ? 'd-none' : ''}>Reset Password</span>
              </button>
            </form>
          </Col>
        </Row>
      </Container>
    </Container>
  );
};
